/**
 * Input Component
 *
 * Form input with label and error states.
 */

import { InputHTMLAttributes, forwardRef } from 'react';
import './Input.css';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className = '', ...props }, ref) => {
    const inputClasses = ['input', error ? 'input-error' : '', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="input-wrapper">
        {label && (
          <label className="input-label" htmlFor={props.id}>
            {label}
          </label>
        )}
        <input ref={ref} className={inputClasses} {...props} />
        {error && <span className="input-error-text">{error}</span>}
        {hint && !error && <span className="input-hint">{hint}</span>}
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Amount Input - for currency values
 */
interface AmountInputProps extends Omit<InputProps, 'type'> {
  currency?: string;
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  ({ currency = 'USD', className = '', ...props }, ref) => {
    return (
      <div className="amount-input-wrapper">
        <span className="amount-currency">{currency}</span>
        <input
          ref={ref}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          className={`input amount-input ${className}`}
          {...props}
        />
      </div>
    );
  }
);

AmountInput.displayName = 'AmountInput';

/**
 * Select Component
 */
interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, className = '', ...props }, ref) => {
    const selectClasses = ['select', error ? 'select-error' : '', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="input-wrapper">
        {label && (
          <label className="input-label" htmlFor={props.id}>
            {label}
          </label>
        )}
        <select ref={ref} className={selectClasses} {...props}>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <span className="input-error-text">{error}</span>}
      </div>
    );
  }
);

Select.displayName = 'Select';
